import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, imgUrl, money, SHAPES, METALS, CARATS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { LogOut, RefreshCw, DownloadCloud, Trash2, CheckCircle2, XCircle, Loader2, Sparkles, Images, Plus, Gem, ChevronDown, ChevronUp, Settings, Mail, Wand2, Package } from "lucide-react";
function Orders() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orders");
      setItems(data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const revenue = items.reduce((s, o) => s + (o.amount_cad || 0), 0);

  return (
    <div className="bg-[#FFFFFF] border border-[#E5E5E5] p-7 mb-12">
      <button data-testid="orders-toggle" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between">
        <div className="text-left">
          <h2 className="font-heading text-2xl text-[#0A0A0A] flex items-center gap-2">
            <Package size={18} /> Orders
            <span className="text-[#737373] text-sm font-mono ml-2">{items.length} · ${revenue.toLocaleString()} CAD</span>
          </h2>
          <p className="text-[#737373] text-sm mt-1">Paid Stripe orders from the storefront.</p>
        </div>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {open && (
        <div className="mt-6 space-y-2" data-testid="orders-panel">
          {loading && <p className="text-[#737373] text-sm py-4">Loading…</p>}
          {!loading && items.length === 0 && <p className="text-[#737373] text-sm py-6">No paid orders yet.</p>}
          {items.map((o) => (
            <div key={o.id} data-testid={`order-row-${o.id}`} className="border border-[#F0EDE7] p-4 grid md:grid-cols-12 gap-3 items-start text-sm">
              <div className="md:col-span-3">
                <div className="font-mono text-[#0A0A0A]" data-testid={`order-id-${o.id}`}>#{o.id.slice(0,8).toUpperCase()}</div>
                <div className="text-[#737373] text-xs">{o.email || "—"}</div>
                <div className="text-[#737373] text-xs mt-1 font-mono">{o.paid_at ? new Date(o.paid_at).toLocaleString() : "—"}</div>
              </div>
              <div className="md:col-span-6 space-y-1">
                {(o.items || []).map((li, idx) => (
                  <div key={idx} className="text-[#525252] text-xs">
                    {li.qty}× <span className="text-[#0A0A0A]">{li.product_name}</span>
                    <span className="text-[#737373]"> · {li.metal} · {li.carat}ct · ${li.line_total_cad}</span>
                  </div>
                ))}
              </div>
              <div className="md:col-span-3 text-right">
                <div className="font-mono text-[#0A0A0A] text-lg">${(o.amount_cad || 0).toLocaleString()}</div>
                <div className="label-track text-[#C8A165] text-[10px]">CAD · Paid</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

import { ImageManager } from "../components/ImageManager";

function SiteSettings() {
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/site/settings").then(({ data }) => setS(data)).catch(() => {});
    api.get("/shape-availability").then(({ data }) => {
      setS((prev) => (prev ? { ...prev, __all_shapes: data.all_shapes } : { __all_shapes: data.all_shapes }));
    }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      // strip the client-only helper key before saving
      const { __all_shapes, ...clean } = s;
      const { data } = await api.put("/admin/site/settings", clean);
      setS({ ...data, __all_shapes });
      toast("Site settings saved.");
    } catch {
      toast("Save failed.");
    }
    setBusy(false);
  };

  const seedTags = async () => {
    if (!window.confirm("Randomly tag 6 products for each tab (Best Sellers / New Arrivals / On Sale) for testing? Existing tag flags will be reset.")) return;
    try {
      const { data } = await api.post("/admin/products/seed-tags");
      toast(`Tagged ${data.bestseller} bestsellers, ${data.new_arrival} new, ${data.on_sale} on sale.`);
    } catch {
      toast("Could not seed tags.");
    }
  };

  return (
    <div className="bg-[#FFFFFF] border border-[#E5E5E5] p-7 mb-12">
      <button data-testid="site-settings-toggle" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between">
        <div className="text-left">
          <h2 className="font-heading text-2xl text-[#0A0A0A] flex items-center gap-2">
            <Settings size={18} /> Site Settings
          </h2>
          <p className="text-[#737373] text-sm mt-1">
            Consultation booking URL, hero video and homepage tag seeding.
          </p>
        </div>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {open && s && (
        <div className="mt-8 grid md:grid-cols-2 gap-6" data-testid="site-settings-panel">
          <div>
            <label className="label-track text-[#737373] text-[10px]">Consultation URL (Calendly / Cal.com)</label>
            <input
              data-testid="setting-consult-url"
              value={s.consultation_url || ""}
              onChange={(e) => setS({ ...s, consultation_url: e.target.value })}
              placeholder="https://cal.com/dreamonds"
              className="bg-transparent border-b border-[#E5E5E5] focus:border-[#0A0A0A] text-[#0A0A0A] w-full py-2 outline-none mt-1"
            />
          </div>
          <div>
            <label className="label-track text-[#737373] text-[10px]">Hero video URL (mp4 · optional)</label>
            <input
              data-testid="setting-hero-video"
              value={s.hero_video_url || ""}
              onChange={(e) => setS({ ...s, hero_video_url: e.target.value })}
              placeholder="https://…/hero.mp4"
              className="bg-transparent border-b border-[#E5E5E5] focus:border-[#0A0A0A] text-[#0A0A0A] w-full py-2 outline-none mt-1"
            />
          </div>
          <div>
            <label className="label-track text-[#737373] text-[10px]">Instagram URL</label>
            <input
              data-testid="setting-instagram-url"
              value={s.instagram_url || ""}
              onChange={(e) => setS({ ...s, instagram_url: e.target.value })}
              placeholder="https://instagram.com/dreamonds"
              className="bg-transparent border-b border-[#E5E5E5] focus:border-[#0A0A0A] text-[#0A0A0A] w-full py-2 outline-none mt-1"
            />
          </div>
          <div className="md:col-span-2 pt-3 grid sm:grid-cols-2 gap-4">
            <label className="flex items-start gap-3 p-4 border border-[#EFE7D8] cursor-pointer hover:border-[#0A0A0A] transition-colors">
              <input
                type="checkbox"
                data-testid="setting-show-build"
                checked={!!s.show_build_a_ring}
                onChange={(e) => setS({ ...s, show_build_a_ring: e.target.checked })}
                className="mt-1"
              />
              <div>
                <div className="font-heading text-lg text-[#0A0A0A]">Show "Build Your Ring"</div>
                <div className="text-xs text-[#737373] mt-1">
                  Displays the Build Your Ring option in the hamburger menu. Currently hidden by default.
                </div>
              </div>
            </label>
            <label className="flex items-start gap-3 p-4 border border-[#EFE7D8] cursor-pointer hover:border-[#0A0A0A] transition-colors">
              <input
                type="checkbox"
                data-testid="setting-show-testimonials"
                checked={!!s.show_testimonials}
                onChange={(e) => setS({ ...s, show_testimonials: e.target.checked })}
                className="mt-1"
              />
              <div>
                <div className="font-heading text-lg text-[#0A0A0A]">Show Testimonials on Homepage</div>
                <div className="text-xs text-[#737373] mt-1">
                  Once you have real approved product reviews, turn this on to display them on the homepage.
                </div>
              </div>
            </label>
          </div>
          <div className="md:col-span-2 pt-4 border-t border-[#EFE7D8]">
            <p className="label-track text-[#737373] text-[10px] mb-3">Diamond shape filter (Shop → Rings)</p>
            <p className="text-xs text-[#737373] mb-3 leading-relaxed">
              Auto = show only when at least one published ring uses this shape. Force show / Force hide overrides that.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {(s.__all_shapes || []).map((shape) => {
                const current = (s.shape_overrides || {})[shape] || "auto";
                return (
                  <div
                    key={shape}
                    className="flex items-center justify-between border border-[#EFE7D8] px-3 py-2"
                    data-testid={`shape-override-${shape.toLowerCase()}`}
                  >
                    <span className="text-sm text-[#0A0A0A]">{shape}</span>
                    <select
                      value={current}
                      onChange={(e) => {
                        const next = { ...(s.shape_overrides || {}) };
                        if (e.target.value === "auto") delete next[shape];
                        else next[shape] = e.target.value;
                        setS({ ...s, shape_overrides: next });
                      }}
                      className="text-xs bg-transparent border border-[#E5E5E5] px-2 py-1 outline-none"
                    >
                      <option value="auto">Auto</option>
                      <option value="show">Force show</option>
                      <option value="hide">Force hide</option>
                    </select>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="md:col-span-2 flex items-center gap-4 flex-wrap pt-3">
            <button
              data-testid="save-site-settings"
              onClick={save}
              disabled={busy}
              className="bg-[#0A0A0A] text-white px-6 py-3 label-track hover:bg-[#C8A165] transition-colors disabled:opacity-50"
            >
              {busy ? "Saving…" : "Save Settings"}
            </button>
            <button
              data-testid="seed-tags-btn"
              onClick={seedTags}
              className="border border-[#E5E5E5] text-[#0A0A0A] px-6 py-3 label-track hover:border-[#C8A165] hover:text-[#C8A165] transition-colors flex items-center gap-2"
            >
              <Wand2 size={14} /> Randomly Tag Products
            </button>
            <p className="text-[#737373] text-xs">
              Tip: manually toggle Best Seller / New / On Sale per product in the catalog below.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function QuoteRequests() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/quotes");
      setItems(data);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = async (id, status) => {
    await api.patch(`/admin/quotes/${id}`, { status });
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    await api.delete(`/admin/quotes/${id}`);
    setItems((prev) => prev.filter((x) => x.id !== id));
  };

  const newCount = items.filter((x) => x.status === "new").length;

  return (
    <div className="bg-[#FFFFFF] border border-[#E5E5E5] p-7 mb-12">
      <button data-testid="quotes-toggle" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between">
        <div className="text-left">
          <h2 className="font-heading text-2xl text-[#0A0A0A] flex items-center gap-2">
            <Mail size={18} /> Quote Requests
            {newCount > 0 && (
              <span className="bg-[#C8A165] text-white text-[10px] font-mono px-2 py-1">{newCount} new</span>
            )}
          </h2>
          <p className="text-[#737373] text-sm mt-1">Price-match submissions from the storefront.</p>
        </div>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {open && (
        <div className="mt-6 space-y-3" data-testid="quotes-panel">
          {items.length === 0 && <p className="text-[#737373] text-sm py-6">No quote requests yet.</p>}
          {items.map((q) => (
            <div key={q.id} data-testid={`quote-row-${q.id}`} className="border border-[#F0EDE7] p-4 grid md:grid-cols-12 gap-3 items-start text-sm">
              <div className="md:col-span-3">
                <div className="text-[#0A0A0A] font-medium">{q.name}</div>
                <div className="text-[#737373] text-xs">{q.email}{q.phone ? ` · ${q.phone}` : ""}</div>
                <div className="text-[#737373] text-xs mt-1 font-mono">{new Date(q.created_at).toLocaleString()}</div>
              </div>
              <div className="md:col-span-6 space-y-1">
                <a href={q.competitor_url} target="_blank" rel="noopener noreferrer" className="text-[#C8A165] hover:underline break-all text-xs">
                  {q.competitor_url}
                </a>
                {q.notes && <p className="text-[#525252] text-xs italic">"{q.notes}"</p>}
              </div>
              <div className="md:col-span-3 flex items-center gap-2 flex-wrap">
                <select
                  data-testid={`quote-status-${q.id}`}
                  value={q.status}
                  onChange={(e) => update(q.id, e.target.value)}
                  className="border border-[#E5E5E5] text-xs px-2 py-1 outline-none bg-white"
                >
                  <option value="new">New</option>
                  <option value="reviewing">Reviewing</option>
                  <option value="sent">Sent</option>
                  <option value="closed">Closed</option>
                </select>
                <button data-testid={`quote-delete-${q.id}`} onClick={() => remove(q.id)} className="text-[#737373] hover:text-[#B76E79] p-1">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function ReviewsPanel() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [products, setProducts] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/reviews", filter ? { params: { status: filter } } : {});
      setItems(data);
    } catch {
      // ignore
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/admin/products").then(({ data }) => {
      const m = {};
      data.forEach((p) => { m[p.id] = p.name; });
      setProducts(m);
    }).catch(() => {});
  }, []);

  const update = async (id, status) => {
    await api.patch(`/admin/reviews/${id}`, { status });
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    await api.delete(`/admin/reviews/${id}`);
    setItems((prev) => prev.filter((x) => x.id !== id));
  };

  const pendingCount = items.filter((x) => x.status === "pending").length;

  return (
    <div className="bg-[#FFFFFF] border border-[#E5E5E5] p-7 mb-12">
      <button data-testid="reviews-toggle" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between">
        <div className="text-left">
          <h2 className="font-heading text-2xl text-[#0A0A0A] flex items-center gap-2">
            <Sparkles size={18} /> Product Reviews
            {pendingCount > 0 && (
              <span className="bg-[#C8A165] text-white text-[10px] font-mono px-2 py-1">{pendingCount} pending</span>
            )}
          </h2>
          <p className="text-[#737373] text-sm mt-1">Approve, reject, or remove customer reviews.</p>
        </div>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {open && (
        <div className="mt-6" data-testid="reviews-panel">
          <div className="flex items-center gap-2 mb-4">
            {["", "pending", "approved", "rejected"].map((k) => (
              <button
                key={k || "all"}
                onClick={() => setFilter(k)}
                data-testid={`reviews-filter-${k || "all"}`}
                className={`px-3 py-1.5 label-track text-[10px] border ${
                  filter === k ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] text-[#737373] hover:border-[#0A0A0A] hover:text-[#0A0A0A]"
                }`}
              >
                {k || "All"}
              </button>
            ))}
          </div>
          {items.length === 0 && <p className="text-[#737373] text-sm py-6">No reviews yet.</p>}
          <div className="space-y-3">
            {items.map((r) => (
              <div key={r.id} data-testid={`review-row-${r.id}`} className="border border-[#F0EDE7] p-4 grid md:grid-cols-12 gap-3 items-start text-sm">
                <div className="md:col-span-3">
                  <div className="text-[#0A0A0A] font-medium">{r.name}</div>
                  <div className="text-[#737373] text-xs">{r.email}</div>
                  <div className="text-[#737373] text-xs mt-1 font-mono">{new Date(r.created_at).toLocaleString()}</div>
                  <div className="text-[#C8A165] text-xs mt-1">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</div>
                </div>
                <div className="md:col-span-6">
                  <div className="text-[#737373] text-xs mb-1">
                    On: <span className="text-[#0A0A0A]">{products[r.product_id] || r.product_id.slice(0, 8)}</span>
                  </div>
                  {r.title && <div className="font-heading text-lg text-[#0A0A0A]">{r.title}</div>}
                  <p className="text-[#525252]">{r.body}</p>
                </div>
                <div className="md:col-span-3 flex items-center gap-2 flex-wrap">
                  <select
                    data-testid={`review-status-${r.id}`}
                    value={r.status}
                    onChange={(e) => update(r.id, e.target.value)}
                    className="border border-[#E5E5E5] text-xs px-2 py-1 outline-none bg-white"
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                  <button data-testid={`review-delete-${r.id}`} onClick={() => remove(r.id)} className="text-[#737373] hover:text-[#B76E79] p-1">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ResizeRequestsPanel() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/resize-requests");
      setItems(data);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = async (id, status) => {
    await api.patch(`/admin/resize-requests/${id}`, { status });
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    await api.delete(`/admin/resize-requests/${id}`);
    setItems((prev) => prev.filter((x) => x.id !== id));
  };

  const newCount = items.filter((x) => x.status === "new").length;

  return (
    <div className="bg-[#FFFFFF] border border-[#E5E5E5] p-7 mb-12">
      <button data-testid="resize-toggle" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between">
        <div className="text-left">
          <h2 className="font-heading text-2xl text-[#0A0A0A] flex items-center gap-2">
            <Mail size={18} /> Ring Resize Requests
            {newCount > 0 && (
              <span className="bg-[#C8A165] text-white text-[10px] font-mono px-2 py-1">{newCount} new</span>
            )}
          </h2>
          <p className="text-[#737373] text-sm mt-1">Complimentary 60 day resize submissions.</p>
        </div>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {open && (
        <div className="mt-6 space-y-3" data-testid="resize-panel">
          {items.length === 0 && <p className="text-[#737373] text-sm py-6">No resize requests yet.</p>}
          {items.map((q) => (
            <div key={q.id} data-testid={`resize-row-${q.id}`} className="border border-[#F0EDE7] p-4 grid md:grid-cols-12 gap-3 items-start text-sm">
              <div className="md:col-span-3">
                <div className="text-[#0A0A0A] font-medium">{q.name}</div>
                <div className="text-[#737373] text-xs">{q.email}{q.phone ? ` · ${q.phone}` : ""}</div>
                <div className="text-[#737373] text-xs mt-1 font-mono">{new Date(q.created_at).toLocaleString()}</div>
              </div>
              <div className="md:col-span-6 text-xs text-[#525252] space-y-1">
                {q.order_number && <div>Order: <span className="text-[#0A0A0A] font-mono">{q.order_number}</span></div>}
                {q.product_description && <div>Ring: <span className="text-[#0A0A0A]">{q.product_description}</span></div>}
                <div>Size: <span className="text-[#0A0A0A] font-mono">{q.current_size || "?"} → {q.desired_size || "?"}</span></div>
                {q.notes && <p className="italic text-[#737373]">"{q.notes}"</p>}
              </div>
              <div className="md:col-span-3 flex items-center gap-2 flex-wrap">
                <select
                  data-testid={`resize-status-${q.id}`}
                  value={q.status}
                  onChange={(e) => update(q.id, e.target.value)}
                  className="border border-[#E5E5E5] text-xs px-2 py-1 outline-none bg-white"
                >
                  <option value="new">New</option>
                  <option value="reviewing">Reviewing</option>
                  <option value="in-progress">In progress</option>
                  <option value="completed">Completed</option>
                  <option value="closed">Closed</option>
                </select>
                <button data-testid={`resize-delete-${q.id}`} onClick={() => remove(q.id)} className="text-[#737373] hover:text-[#B76E79] p-1">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function PricingSettings() {
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings/pricing").then(({ data }) => setS(data)).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings/pricing", {
        exchange_rate: Number(s.exchange_rate),
        markup: Number(s.markup),
        making_fee: Number(s.making_fee),
        metal_rates_per_gram: Object.fromEntries(Object.entries(s.metal_rates_per_gram).map(([k, v]) => [k, Number(v)])),
        category_weights: Object.fromEntries(Object.entries(s.category_weights).map(([k, v]) => [k, Number(v)])),
      });
      setS(data);
      toast("Pricing settings saved. all prices updated.");
    } catch {
      toast("Save failed.");
    }
    setBusy(false);
  };

  const field = (label, value, onChange, testid) => (
    <div key={testid}>
      <label className="label-track text-[#737373] text-[10px]">{label}</label>
      <input data-testid={testid} type="number" step="0.01" value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border-b border-[#E5E5E5] focus:border-[#0A0A0A] text-[#0A0A0A] w-full py-1 outline-none mt-1" />
    </div>
  );

  return (
    <div className="bg-[#FFFFFF] border border-[#E5E5E5] p-7 mb-12">
      <button data-testid="pricing-settings-toggle" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between">
        <div className="text-left">
          <h2 className="font-heading text-2xl text-[#0A0A0A] flex items-center gap-2"><Gem size={18} className="text-[#0A0A0A]" /> Pricing Engine</h2>
          <p className="text-[#737373] text-sm mt-1">
            {s ? `FX ${s.exchange_rate} · Markup ${s.markup}x · Making fee $${s.making_fee} CAD` : "Loading…"}
          </p>
        </div>
        {open ? <ChevronUp size={18} className="text-[#737373]" /> : <ChevronDown size={18} className="text-[#737373]" />}
      </button>

      {open && s && (
        <div className="mt-7">
          <div className="grid sm:grid-cols-3 gap-6">
            {field("USD → CAD Exchange Rate", s.exchange_rate, (v) => setS({ ...s, exchange_rate: v }), "setting-exchange-rate")}
            {field("Retail Markup (×)", s.markup, (v) => setS({ ...s, markup: v }), "setting-markup")}
            {field("Making Fee (CAD)", s.making_fee, (v) => setS({ ...s, making_fee: v }), "setting-making-fee")}
          </div>
          <p className="label-track text-[#0A0A0A] text-[10px] mt-8 mb-3">Metal rates. CAD per gram</p>
          <div className="grid sm:grid-cols-3 gap-6">
            {METALS.map((m) =>
              field(`${m.label} ($/g)`, s.metal_rates_per_gram[m.id],
                (v) => setS({ ...s, metal_rates_per_gram: { ...s.metal_rates_per_gram, [m.id]: v } }),
                `setting-rate-${m.id}`))}
          </div>
          <p className="label-track text-[#0A0A0A] text-[10px] mt-8 mb-3">Default gram weight per category</p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-6">
            {Object.entries(s.category_weights).map(([cat, w]) =>
              field(`${cat} (g)`, w,
                (v) => setS({ ...s, category_weights: { ...s.category_weights, [cat]: v } }),
                `setting-weight-${cat}`))}
          </div>
          <div className="flex justify-end mt-8">
            <button data-testid="save-pricing-settings" onClick={save} disabled={busy}
              className="bg-[#0A0A0A] text-white px-8 py-3 label-track text-[10px] hover:bg-[#333333] disabled:opacity-50">
              {busy ? "Saving…" : "Save Pricing Settings"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Breakdown({ productId }) {
  const [metal, setMetal] = useState("silver");
  const [carat, setCarat] = useState(1);
  const [b, setB] = useState(null);

  useEffect(() => {
    api.get(`/admin/products/${productId}/pricing-breakdown`, { params: { metal, carat } })
      .then(({ data }) => setB(data)).catch(() => {});
  }, [productId, metal, carat]);

  return (
    <div data-testid={`breakdown-${productId}`} className="mt-4 bg-[#F7F7F9] border border-[#E5E5E5] p-4 text-xs">
      <div className="flex items-center gap-3 mb-3">
        <select value={metal} onChange={(e) => setMetal(e.target.value)}
          className="bg-[#FFFFFF] border border-[#E5E5E5] text-[#0A0A0A] px-2 py-1 outline-none">
          {METALS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
        <select value={carat} onChange={(e) => setCarat(Number(e.target.value))}
          className="bg-[#FFFFFF] border border-[#E5E5E5] text-[#0A0A0A] px-2 py-1 outline-none">
          {CARATS.map((c) => <option key={c} value={c}>{c} ct</option>)}
        </select>
      </div>
      {b ? (
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 font-mono text-[#0A0A0A]">
          <div><span className="text-[#737373] block">Diamond</span>{money(b.diamond_cad)}</div>
          <div><span className="text-[#737373] block">Metal ({b.gram_weight}g)</span>{money(b.metal_cad)}</div>
          <div><span className="text-[#737373] block">Making</span>{money(b.making_fee)}</div>
          <div><span className="text-[#737373] block">Base cost</span>{money(b.base_cost)}</div>
          <div><span className="text-[#737373] block">Margin</span>{money(b.margin)}</div>
          <div><span className="text-[#737373] block">Retail ({b.markup}x)</span><span className="text-[#0A0A0A]">{money(b.retail)}</span></div>
        </div>
      ) : <span className="text-[#737373]">Loading…</span>}
    </div>
  );
}

function ProductRow({ p, onSaved, onDeleted, onManage }) {
  const [edit, setEdit] = useState({
    name: p.name,
    featured: p.featured,
    published: p.published,
    default_color: p.default_color,
    shape: p.shape || "Round",
    gram_weight: p.gram_weight || "",
    making_fee: p.making_fee || "",
    is_bestseller: !!p.is_bestseller,
    is_new_arrival: !!p.is_new_arrival,
    is_on_sale: !!p.is_on_sale,
  });
  const [busy, setBusy] = useState(false);
  const [reclassing, setReclassing] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: edit.name,
        featured: edit.featured,
        published: edit.published,
        default_color: edit.default_color,
        shape: edit.shape,
        is_bestseller: edit.is_bestseller,
        is_new_arrival: edit.is_new_arrival,
        is_on_sale: edit.is_on_sale,
      };
      payload.gram_weight = edit.gram_weight === "" ? 0 : Number(edit.gram_weight);
      payload.making_fee = edit.making_fee === "" ? 0 : Number(edit.making_fee);
      const { data } = await api.patch(`/admin/products/${p.id}`, payload);
      onSaved(data);
      toast("Saved.");
    } catch {
      toast("Save failed.");
    }
    setBusy(false);
  };

  const reclassify = async () => {
    setReclassing(true);
    try {
      const { data } = await api.post(`/admin/products/${p.id}/reclassify`);
      onSaved(data);
      toast("Colors re-detected.");
    } catch (e) {
      toast(e.response?.data?.detail || "Re-detect failed.");
    }
    setReclassing(false);
  };

  return (
    <div data-testid={`admin-product-${p.id}`} className="bg-[#FFFFFF] border border-[#E5E5E5] p-5 flex gap-5">
      <div className="w-24 h-28 bg-[#F7F7F9] overflow-hidden flex-shrink-0">
        {p.variants?.[0]?.images?.[0] && (
          <img src={imgUrl(p.variants[0].images[0])} alt={p.name} className="w-full h-full object-cover" />
        )}
      </div>
      <div className="flex-1 grid sm:grid-cols-2 gap-4">
        <div>
          <label className="label-track text-[#737373] text-[10px]">Name</label>
          <input
            data-testid={`edit-name-${p.id}`}
            value={edit.name}
            onChange={(e) => setEdit({ ...edit, name: e.target.value })}
            className="bg-transparent border-b border-[#E5E5E5] focus:border-[#0A0A0A] text-[#0A0A0A] w-full py-1 outline-none mt-1"
          />
          <p className="text-[10px] text-[#737373] mt-1 font-mono">
            {p.category_label} · {p.product_code} · From {money(p.price)} · {p.variants?.map((v) => `${v.color}(${v.images.length})`).join(" / ")}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="label-track text-[#737373] text-[10px]">Shape {p.shape_confident === false && <span className="text-[#B76E79]">?</span>}</label>
            <select
              data-testid={`edit-shape-${p.id}`}
              value={edit.shape}
              onChange={(e) => setEdit({ ...edit, shape: e.target.value })}
              className="bg-[#FFFFFF] border border-[#E5E5E5] text-[#0A0A0A] text-sm px-2 py-1 outline-none w-full mt-1"
            >
              {SHAPES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="label-track text-[#737373] text-[10px]">Grams</label>
            <input
              data-testid={`edit-grams-${p.id}`}
              type="number" step="0.5" value={edit.gram_weight} placeholder="default"
              onChange={(e) => setEdit({ ...edit, gram_weight: e.target.value })}
              className="bg-transparent border-b border-[#E5E5E5] focus:border-[#0A0A0A] text-[#0A0A0A] w-full py-1 outline-none mt-1"
            />
          </div>
          <div>
            <label className="label-track text-[#737373] text-[10px]">Making fee</label>
            <input
              data-testid={`edit-fee-${p.id}`}
              type="number" value={edit.making_fee} placeholder="default"
              onChange={(e) => setEdit({ ...edit, making_fee: e.target.value })}
              className="bg-transparent border-b border-[#E5E5E5] focus:border-[#0A0A0A] text-[#0A0A0A] w-full py-1 outline-none mt-1"
            />
          </div>
        </div>
        <div className="flex items-center gap-5 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-[#737373] cursor-pointer">
            <input type="checkbox" checked={edit.featured} onChange={(e) => setEdit({ ...edit, featured: e.target.checked })} />
            Featured
          </label>
          <label className="flex items-center gap-2 text-sm text-[#737373] cursor-pointer">
            <input type="checkbox" checked={edit.published} onChange={(e) => setEdit({ ...edit, published: e.target.checked })} />
            Published
          </label>
          <label className="flex items-center gap-2 text-sm text-[#737373] cursor-pointer" data-testid={`edit-bestseller-${p.id}`}>
            <input type="checkbox" checked={edit.is_bestseller} onChange={(e) => setEdit({ ...edit, is_bestseller: e.target.checked })} />
            Best Seller
          </label>
          <label className="flex items-center gap-2 text-sm text-[#737373] cursor-pointer" data-testid={`edit-new-${p.id}`}>
            <input type="checkbox" checked={edit.is_new_arrival} onChange={(e) => setEdit({ ...edit, is_new_arrival: e.target.checked })} />
            New Arrival
          </label>
          <label className="flex items-center gap-2 text-sm text-[#737373] cursor-pointer" data-testid={`edit-sale-${p.id}`}>
            <input type="checkbox" checked={edit.is_on_sale} onChange={(e) => setEdit({ ...edit, is_on_sale: e.target.checked })} />
            On Sale
          </label>
          <select
            value={edit.default_color}
            onChange={(e) => setEdit({ ...edit, default_color: e.target.value })}
            className="bg-[#FFFFFF] border border-[#E5E5E5] text-[#0A0A0A] text-sm px-2 py-1 outline-none"
          >
            {p.variants?.map((v) => <option key={v.color} value={v.color}>{v.color}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-3 justify-end flex-wrap">
          <button data-testid={`breakdown-toggle-${p.id}`} onClick={() => setShowBreakdown((s) => !s)}
            className="border border-[#E5E5E5] text-[#0A0A0A] px-4 py-2 label-track text-[10px] hover:border-[#0A0A0A] hover:text-[#0A0A0A] flex items-center gap-1">
            <Gem size={12} /> Pricing
          </button>
          <button data-testid={`manage-images-${p.id}`} onClick={() => onManage(p)}
            className="border border-[#E5E5E5] text-[#0A0A0A] px-4 py-2 label-track text-[10px] hover:border-[#0A0A0A] hover:text-[#0A0A0A] flex items-center gap-1">
            <Images size={12} /> Images
          </button>
          <button data-testid={`reclassify-product-${p.id}`} onClick={reclassify} disabled={reclassing}
            className="border border-[#E5E5E5] text-[#0A0A0A] px-4 py-2 label-track text-[10px] hover:border-[#0A0A0A] flex items-center gap-1 disabled:opacity-50">
            {reclassing ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {reclassing ? "Detecting…" : "Re-detect colors"}
          </button>
          <button data-testid={`save-product-${p.id}`} onClick={save} disabled={busy}
            className="bg-[#0A0A0A] text-white px-5 py-2 label-track text-[10px] hover:bg-[#333333] disabled:opacity-50">
            {busy ? "Saving…" : "Save"}
          </button>
          <button data-testid={`delete-product-${p.id}`} onClick={() => onDeleted(p.id)}
            className="text-[#737373] hover:text-[#B76E79]">
            <Trash2 size={17} />
          </button>
        </div>
        {showBreakdown && (
          <div className="sm:col-span-2">
            <Breakdown productId={p.id} />
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [drive, setDrive] = useState(null);
  const [job, setJob] = useState(null);
  const [importing, setImporting] = useState(false);
  const [managing, setManaging] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: "", category: "rings", price: "" });
  const [unclearOnly, setUnclearOnly] = useState(false);
  const pollRef = useRef(null);

  const loadProducts = useCallback(() => {
    api.get("/admin/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const verify = async () => {
    setDrive({ loading: true });
    try {
      const { data } = await api.get("/admin/drive/verify");
      setDrive(data);
    } catch (e) {
      setDrive({ ok: false, message: e.response?.data?.detail || e.message });
    }
  };

  const pollJob = (jobId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/import/${jobId}`);
        setJob(data);
        if (data.status !== "running") {
          clearInterval(pollRef.current);
          setImporting(false);
          loadProducts();
          toast(data.status === "done" ? "Import complete." : "Import ended with errors.");
        }
      } catch {
        clearInterval(pollRef.current);
        setImporting(false);
      }
    }, 2000);
  };

  const startImport = async () => {
    setImporting(true);
    setJob(null);
    try {
      const { data } = await api.post("/admin/import", { mode: "test" });
      setJob(data);
      pollJob(data.id);
    } catch (e) {
      setImporting(false);
      toast(e.response?.data?.detail || "Could not start import");
    }
  };

  const startFullImport = async () => {
    if (!window.confirm("Import the FULL catalog (all Rings, Necklaces & Earrings)? This runs in the background and can take several minutes.")) return;
    setImporting(true);
    setJob(null);
    try {
      const { data } = await api.post("/admin/import", { mode: "full" });
      setJob(data);
      pollJob(data.id);
    } catch (e) {
      setImporting(false);
      toast(e.response?.data?.detail || "Could not start import");
    }
  };

  const onDeleted = async (id) => {
    await api.delete(`/admin/products/${id}`);
    setProducts((prev) => prev.filter((p) => p.id !== id));
    toast("Deleted.");
  };

  const createProduct = async () => {
    if (!newProduct.name.trim()) {
      toast("Give the piece a name.");
      return;
    }
    try {
      const { data } = await api.post("/admin/products", {
        name: newProduct.name.trim(),
        category: newProduct.category,
        price: newProduct.price ? Number(newProduct.price) : null,
      });
      setProducts((prev) => [data, ...prev]);
      setCreating(false);
      setNewProduct({ name: "", category: "rings", price: "" });
      setManaging(data); // jump straight into image upload
      toast("Product created. add its images.");
    } catch (e) {
      toast(e.response?.data?.detail || "Could not create product.");
    }
  };

  const onProductUpdated = (data) => {
    setProducts((prev) => prev.map((x) => (x.id === data.id ? data : x)));
    setManaging((m) => (m && m.id === data.id ? data : m));
  };

  const unclearCount = products.filter((p) => p.shape_confident === false).length;
  const visibleProducts = unclearOnly ? products.filter((p) => p.shape_confident === false) : products;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen pt-28 max-w-[1400px] mx-auto px-6 sm:px-12 pb-24">
      <div className="flex items-center justify-between mb-12">
        <div>
          <p className="label-track text-[#0A0A0A]">Dreamonds Studio</p>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl text-[#0A0A0A] mt-2">Catalog Management</h1>
        </div>
        <div className="flex items-center gap-5">
          <button onClick={() => navigate("/")} className="label-track text-[#737373] hover:text-[#0A0A0A]">View Site</button>
          <button data-testid="admin-logout" onClick={() => { logout(); navigate("/"); }}
            className="label-track text-[#737373] hover:text-[#0A0A0A] flex items-center gap-2">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </div>

      {/* SITE SETTINGS */}
      <SiteSettings />

      {/* ORDERS */}
      <Orders />

      {/* QUOTE REQUESTS */}
      <QuoteRequests />

      {/* REVIEWS */}
      <ReviewsPanel />

      {/* RING RESIZE REQUESTS */}
      <ResizeRequestsPanel />

      {/* PRICING SETTINGS */}
      <PricingSettings />

      {/* IMPORT PANEL */}
      <div className="bg-[#FFFFFF] border border-[#E5E5E5] p-7 mb-12">
        <h2 className="font-heading text-2xl text-[#0A0A0A] mb-2">Google Drive Import</h2>
        <p className="text-[#737373] text-sm mb-6 max-w-2xl">
          Imports images only (skipping CAD, STL &amp; video), auto-detects Gold / Rose Gold / Silver,
          and builds color variants. Test mode pulls the first product from Rings, Necklaces &amp; Earrings.
        </p>

        <div className="flex flex-wrap gap-4 mb-6">
          <button data-testid="verify-drive-button" onClick={verify}
            className="border border-[#E5E5E5] text-[#0A0A0A] px-6 py-3 label-track hover:border-[#0A0A0A] hover:text-[#0A0A0A] flex items-center gap-2">
            <RefreshCw size={14} /> Verify Connection
          </button>
          <button data-testid="import-test-button" onClick={startImport} disabled={importing}
            className="border border-[#0A0A0A] text-[#0A0A0A] px-6 py-3 label-track hover:bg-[#0A0A0A] hover:text-white flex items-center gap-2 disabled:opacity-50">
            {importing ? <Loader2 size={14} className="animate-spin" /> : <DownloadCloud size={14} />}
            Test (1 / category)
          </button>
          <button data-testid="import-full-button" onClick={startFullImport} disabled={importing}
            className="bg-[#0A0A0A] text-white px-6 py-3 label-track hover:bg-[#333333] flex items-center gap-2 disabled:opacity-50">
            {importing ? <Loader2 size={14} className="animate-spin" /> : <DownloadCloud size={14} />}
            {importing ? "Importing…" : "Import Full Catalog"}
          </button>
        </div>

        {drive && !drive.loading && (
          <div data-testid="drive-status" className={`flex items-start gap-2 text-sm mb-4 ${drive.ok ? "text-[#2A5A3B]" : "text-[#B76E79]"}`}>
            {drive.ok ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
            <span className={drive.ok ? "text-emerald-400" : "text-[#B76E79]"}>{drive.message}</span>
          </div>
        )}

        {job && (
          <div data-testid="import-job" className="mt-4 bg-[#F7F7F9] border border-[#E5E5E5] p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="label-track text-[#737373]">
                Status: <span className="text-[#0A0A0A]">{job.status}</span>
                {job.total > 0 && <span className="text-[#737373]"> · {job.processed}/{job.total} products</span>}
              </span>
              {job.products_created > 0 && <span className="text-emerald-400 text-sm">{job.products_created} created</span>}
            </div>
            <div className="max-h-44 overflow-y-auto font-mono text-xs text-[#737373] space-y-1">
              {job.log?.map((l, i) => <div key={i}>{l}</div>)}
            </div>
          </div>
        )}
      </div>

      {/* PRODUCTS */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-5">
          <h2 className="font-heading text-2xl text-[#0A0A0A]">
            Products ({visibleProducts.length}{unclearOnly ? ` of ${products.length}` : ""})
          </h2>
          {unclearCount > 0 && (
            <button
              data-testid="filter-unclear-shapes"
              onClick={() => setUnclearOnly((v) => !v)}
              className={`px-4 py-1.5 label-track text-[10px] border transition-colors ${
                unclearOnly
                  ? "border-[#B76E79] bg-[#B76E79] text-white"
                  : "border-[#E5E5E5] text-[#B76E79] hover:border-[#B76E79]"
              }`}
            >
              AI unsure shapes ({unclearCount})
            </button>
          )}
        </div>
        <div className="flex items-center gap-5">
          <button data-testid="new-product-button" onClick={() => setCreating(true)}
            className="bg-[#0A0A0A] text-white px-5 py-2 label-track text-[10px] hover:bg-[#333333] flex items-center gap-2">
            <Plus size={14} /> New Product
          </button>
          <button onClick={loadProducts} className="label-track text-[#737373] hover:text-[#0A0A0A] flex items-center gap-2">
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {products.length === 0 && (
        <p className="text-[#737373] text-sm py-10 text-center border border-[#E5E5E5]">
          No products yet. Run an import or add one manually.
        </p>
      )}

      <div className="space-y-4">
        {visibleProducts.map((p) => (
          <ProductRow
            key={p.id}
            p={p}
            onSaved={(d) => setProducts((prev) => prev.map((x) => (x.id === d.id ? d : x)))}
            onDeleted={onDeleted}
            onManage={setManaging}
          />
        ))}
      </div>

      {managing && (
        <ImageManager product={managing} onUpdated={onProductUpdated} onClose={() => setManaging(null)} />
      )}

      {creating && (
        <div data-testid="new-product-modal" className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setCreating(false)}>
          <div className="bg-[#F7F7F9] border border-[#E5E5E5] w-full max-w-md p-7 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-2xl text-[#0A0A0A] mb-6">New Product</h3>
            <div className="space-y-5">
              <div>
                <label className="label-track text-[#737373] text-[10px]">Name</label>
                <input data-testid="new-product-name" value={newProduct.name}
                  onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                  className="bg-transparent border-b border-[#E5E5E5] focus:border-[#0A0A0A] text-[#0A0A0A] w-full py-1 outline-none mt-1" />
              </div>
              <div>
                <label className="label-track text-[#737373] text-[10px]">Category</label>
                <select data-testid="new-product-category" value={newProduct.category}
                  onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                  className="bg-[#FFFFFF] border border-[#E5E5E5] text-[#0A0A0A] text-sm px-2 py-2 outline-none w-full mt-1">
                  <option value="rings">Rings</option>
                  <option value="necklaces">Necklaces</option>
                  <option value="earrings">Earrings</option>
                  <option value="ring-bands">Ring Bands</option>
                  <option value="bracelets">Bracelets</option>
                </select>
              </div>
              <div>
                <label className="label-track text-[#737373] text-[10px]">Price (USD, optional)</label>
                <input data-testid="new-product-price" type="number" value={newProduct.price}
                  onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                  className="bg-transparent border-b border-[#E5E5E5] focus:border-[#0A0A0A] text-[#0A0A0A] w-full py-1 outline-none mt-1" />
              </div>
            </div>
            <div className="flex justify-end gap-4 mt-8">
              <button onClick={() => setCreating(false)} className="label-track text-[#737373] hover:text-[#0A0A0A] text-xs">Cancel</button>
              <button data-testid="create-product-submit" onClick={createProduct}
                className="bg-[#0A0A0A] text-white px-6 py-2 label-track text-[10px] hover:bg-[#333333]">Create &amp; add images</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
